import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {createPortal} from 'react-dom';
import {Button} from '../ui/Button';
import {useTableSearch} from '../../hooks/useTableSearch';
import {useDragSelect} from '../../hooks/useDragSelect';
import {useTablePreferences} from '../../hooks/useTablePreferences';
import {TableColumn} from '../../types/table';
import {filterRowsByColumn} from '../../search/filterByColumn';

// Context menu action definition
export interface ContextMenuAction<T = any> {
    label: string;
    icon?: React.ReactNode;
    onClick: (item: T) => void;
    disabled?: (item: T) => boolean;
    hidden?: (item: T) => boolean;
    className?: string;
}

// Filter option for server-side filtering
export interface FilterOption {
    value: string;
    count: number;
}

export interface DataTableProps<T = any> {
    tableId: string;
    data: T[];
    columns: TableColumn<T>[];
    loading?: boolean;
    onLoadMore?: () => void;
    hasMore?: boolean;
    onSearch?: (query: string, column: string) => void;
    onSort?: (column: string, direction: 'asc' | 'desc') => void;
    onSelectionChange?: (selectedIds: string[]) => void;
    onEdit?: (item: T) => void;
    onDelete?: (item: T) => void;
    onBulkDelete?: (selectedIds: string[]) => void;
    onBulkArchive?: (selectedIds: string[]) => void;
    onCreate?: () => void;
    onRowClick?: (item: T) => void;
    onRowDoubleClick?: (item: T) => void;
    selectedRowId?: string;
    scrollToRowId?: string; // Scroll to this row when data loads
    rowKey?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    totalCount?: number;
    pageSize?: number;
    filterMode?: 'client' | 'server';
    // Controlled search props - if provided, use these instead of internal state
    searchQuery?: string;
    searchColumn?: string;
    // Controlled sort props - if provided, use these instead of internal state (server-side sorting)
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc';
    // Custom row className function
    rowClassName?: (row: T, index: number) => string;
    // Show/hide header (useful when parent component provides its own header)
    showHeader?: boolean;
    // Compact mode - smaller padding and text sizes for more columns
    compact?: boolean;
    actions?: {
        create?: boolean;
        edit?: boolean;
        delete?: boolean;
        bulkDelete?: boolean;
        bulkArchive?: boolean;
    };
    // Context menu actions - custom right-click menu items
    contextMenuActions?: ContextMenuAction<T>[];
    // Server-side filtering props
    serverSideFilters?: boolean;
    onFiltersChange?: (filters: Record<string, string[]>) => void;
    getFilterOptions?: (columnKey: string, currentFilters?: Record<string, string[]>) => Promise<FilterOption[]>;
    // Controlled column filters (for server-side filtering)
    columnFilters?: Record<string, string[]>;
}

export const DataTable = <T extends Record<string, any>>({
                                                             tableId,
                                                             data,
                                                             columns,
                                                             loading = false,
                                                             onLoadMore,
                                                             hasMore = false,
                                                             onSearch,
                                                             onRowDoubleClick,
                                                             onSort,
                                                             onSelectionChange,
                                                             onEdit,
                                                             onDelete,
                                                             onBulkDelete,
                                                             onBulkArchive,
                                                             onCreate,
                                                             onRowClick,
                                                             selectedRowId,
                                                             scrollToRowId,
                                                             rowKey = 'id',
                                                             searchPlaceholder,
                                                             emptyMessage = 'No data available',
                                                             totalCount,
                                                             pageSize = 50,
                                                             filterMode,
                                                             searchQuery: controlledSearchQuery,
                                                             searchColumn: controlledSearchColumn,
                                                             sortColumn: controlledSortColumn,
                                                             sortDirection: controlledSortDirection,
                                                             rowClassName,
                                                             showHeader = true,
                                                             compact = false,
                                                             actions = {
                                                                 create: true,
                                                                 edit: true,
                                                                 delete: true,
                                                                 bulkDelete: true,
                                                                 bulkArchive: false
                                                             },
                                                             contextMenuActions,
                                                             serverSideFilters = false,
                                                             onFiltersChange,
                                                             getFilterOptions,
                                                             columnFilters: controlledColumnFilters
                                                         }: DataTableProps<T>) => {
    // Use the table preferences hook for persistent state (including sorting)
    const {
        columnWidths,
        setColumnWidth,
        columnOrder,
        setColumnOrder,
        sortColumn: persistedSortColumn,
        sortDirection: persistedSortDirection,
        handleSort: persistedHandleSort,
        columnFilters: internalColumnFilters,
        setColumnFilter: internalSetColumnFilter,
        clearAllFilters,
    } = useTablePreferences({
        tableId,
        columns,
    });

    // Use controlled column filters if provided, otherwise use internal state
    const columnFilters = controlledColumnFilters ?? internalColumnFilters;
    const setColumnFilter = (key: string, values: string[]) => {
        // Always update internal state for local UI
        internalSetColumnFilter(key, values);
        // If server-side filtering, notify parent of the change
        if (serverSideFilters && onFiltersChange) {
            const newFilters = {...columnFilters, [key]: values};
            onFiltersChange(newFilters);
        }
    };

    // State for server-side filter options
    const [serverFilterOptions, setServerFilterOptions] = useState<FilterOption[]>([]);
    const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);

    // Helper function to determine responsive column class and style
    const getColumnStyle = (column: TableColumn<T>) => {
        const customWidth = columnWidths[column.key];
        if (customWidth) {
            return {width: `${customWidth}px`, minWidth: `${customWidth}px`};
        }

        // Scale down widths in compact mode
        const scale = compact ? 0.75 : 1;

        if (column.width) {
            switch (column.width) {
                case 'xs':
                    return {width: `${64 * scale}px`, minWidth: `${64 * scale}px`};
                case 'sm':
                    return {width: `${80 * scale}px`, minWidth: `${80 * scale}px`};
                case 'md':
                    return {width: `${128 * scale}px`, minWidth: `${128 * scale}px`};
                case 'lg':
                    return {width: `${192 * scale}px`, minWidth: `${192 * scale}px`};
                case 'xl':
                    return {width: `${256 * scale}px`, minWidth: `${256 * scale}px`};
                case 'auto':
                    return {};
                default:
                    return {width: column.width, minWidth: column.width};
            }
        }

        // Default width based on content type (scaled in compact mode)
        if (column.key === 'id') return {width: `${64 * scale}px`, minWidth: `${64 * scale}px`};
        if (column.key === 'code') return {width: `${96 * scale}px`, minWidth: `${96 * scale}px`};
        if (column.key.includes('date') || column.key.includes('time')) return {
            width: `${128 * scale}px`,
            minWidth: `${128 * scale}px`
        };
        if (column.key === 'description' || column.key === 'address') return {minWidth: `${192 * scale}px`};
        if (column.key.includes('weight') || column.key.includes('length') ||
            column.key.includes('width') || column.key.includes('height') ||
            column.key.includes('cbm')) return {width: `${80 * scale}px`, minWidth: `${80 * scale}px`};
        // Remarks column - fixed width, don't expand with content
        if (column.key === 'remarks') return {
            width: `${128 * scale}px`,
            minWidth: `${128 * scale}px`,
            maxWidth: `${128 * scale}px`
        };

        return {minWidth: `${96 * scale}px`}; // Default
    };

    // Get ordered columns
    const getOrderedColumns = () => {
        if (columnOrder.length === 0) return columns;

        const orderedCols = columnOrder
            .map(key => columns.find(col => col.key === key))
            .filter(Boolean) as TableColumn<T>[];

        // Add any missing columns at the end
        const missingCols = columns.filter(col => !columnOrder.includes(col.key));

        return [...orderedCols, ...missingCols];
    };

    // State management
    const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

    // Column drag UI state (not persisted)
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
    const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

    // Drag and select functionality
    const {
        isDraggingRef,
        hasMovedRef,
        handleCheckboxMouseDown,
        handleRowMouseEnter,
        handleRowMouseMove
    } = useDragSelect({
        selectedRows,
        onSelectionChange: (selectedIds) => {
            setSelectedRows(new Set(selectedIds));
            onSelectionChange?.(selectedIds);
        }
    });

    // Column filters UI state (filter values come from useTablePreferences hook)
    const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
    const [filterSearch, setFilterSearch] = useState('');
    const [filterDropdownPosition, setFilterDropdownPosition] = useState<{ top: number; left: number } | null>(null);
    const filterDropdownRef = useRef<HTMLDivElement>(null);

    // Context menu state
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: T } | null>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    // Scroll to row tracking
    const hasScrolledToRowRef = useRef(false);
    const rowRefsMap = useRef<Map<string, HTMLTableRowElement>>(new Map());

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
                setOpenFilterColumn(null);
                setFilterSearch('');
                setFilterDropdownPosition(null);
            }
        };
        if (openFilterColumn) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [openFilterColumn]);

    // Fetch filter options from server when filter dropdown opens
    useEffect(() => {
        if (openFilterColumn && serverSideFilters && getFilterOptions) {
            setLoadingFilterOptions(true);
            setServerFilterOptions([]);
            // Pass current filters to get dynamic options based on other active filters
            getFilterOptions(openFilterColumn, columnFilters)
                .then((options) => {
                    setServerFilterOptions(options);
                })
                .catch((err) => {
                    console.error('Failed to fetch filter options:', err);
                    setServerFilterOptions([]);
                })
                .finally(() => {
                    setLoadingFilterOptions(false);
                });
        }
    }, [openFilterColumn, serverSideFilters, getFilterOptions, columnFilters]);

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
                setContextMenu(null);
            }
        };
        if (contextMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [contextMenu]);

    // Scroll to row when data loads
    useEffect(() => {
        if (scrollToRowId && !hasScrolledToRowRef.current && !loading && data.length > 0) {
            // Check if the row exists in data
            const rowExists = data.some((item: T) => item[rowKey] === scrollToRowId);
            if (rowExists) {
                // Use requestAnimationFrame to ensure DOM is updated
                requestAnimationFrame(() => {
                    const rowElement = rowRefsMap.current.get(scrollToRowId);
                    if (rowElement) {
                        rowElement.scrollIntoView({behavior: 'auto', block: 'center'});
                        hasScrolledToRowRef.current = true;
                    }
                });
            }
        }
    }, [scrollToRowId, data, loading, rowKey]);

    // Use the table search hook for search functionality (only if not controlled)
    const internalSearch = useTableSearch({
        columns,
        onSearchChange: onSearch,
    });

    // Use controlled props if provided, otherwise use internal state
    const searchQuery = controlledSearchQuery ?? internalSearch.searchQuery;
    const searchColumn = controlledSearchColumn ?? internalSearch.searchColumn;
    const setSearchQuery = controlledSearchQuery !== undefined
        ? (query: string) => onSearch?.(query, searchColumn || '')
        : internalSearch.setSearchQuery;
    const handleColumnClick = controlledSearchColumn !== undefined
        ? (columnKey: string) => {
            const column = columns.find(col => col.key === columnKey);
            if (column && column.searchable !== false && onSearch) {
                onSearch(searchQuery, columnKey);
            }
        }
        : internalSearch.handleColumnClick;
    const searchPlaceholderValue = searchPlaceholder || internalSearch.searchPlaceholder;

    // Determine if we're using controlled (server-side) sorting
    const isControlledSort = controlledSortColumn !== undefined;

    // Use controlled sort state if provided, otherwise use persisted state from preferences
    const sortColumn = isControlledSort ? controlledSortColumn : persistedSortColumn;
    const sortDirection = isControlledSort ? (controlledSortDirection || 'asc') : persistedSortDirection;

    // When controlled, data is already sorted server-side; when not controlled, sort client-side
    const sortedData = useMemo(() => {
        if (isControlledSort) return data;
        if (!persistedSortColumn) return data;

        // Sort data client-side using the persisted sort state
        const column = columns.find(c => c.key === persistedSortColumn);
        if (!column) return data;

        return [...data].sort((a, b) => {
            const aValue = column.accessor ? column.accessor(a) : a[persistedSortColumn];
            const bValue = column.accessor ? column.accessor(b) : b[persistedSortColumn];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return persistedSortDirection === 'asc' ? 1 : -1;
            if (bValue == null) return persistedSortDirection === 'asc' ? -1 : 1;

            let comparison = 0;
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                comparison = aValue - bValue;
            } else {
                comparison = String(aValue).localeCompare(String(bValue));
            }

            return persistedSortDirection === 'asc' ? comparison : -comparison;
        });
    }, [data, isControlledSort, persistedSortColumn, persistedSortDirection, columns]);

    // Handle sort - calls onSort callback for server-side, or persists to preferences for client-side
    const handleSort = (columnKey: string) => {
        if (isControlledSort && onSort) {
            // In controlled mode, calculate new direction and call onSort
            const newDirection = sortColumn === columnKey && sortDirection === 'asc' ? 'desc' : 'asc';
            onSort(columnKey, newDirection);
        } else {
            // In uncontrolled mode, use persisted handler (saves to localStorage)
            persistedHandleSort(columnKey);
            // Also call onSort for backward compatibility
            if (onSort) {
                const newDirection = persistedSortColumn === columnKey && persistedSortDirection === 'asc' ? 'desc' : 'asc';
                onSort(columnKey, newDirection);
            }
        }
    };
    const resizeRef = useRef<{
        isResizing: boolean;
        startX: number;
        startWidth: number;
        columnKey: string;
    }>({isResizing: false, startX: 0, startWidth: 0, columnKey: ''});


    // Handle row selection
    const handleRowSelect = useCallback((rowId: string, isSelected: boolean) => {
        const newSelected = new Set(selectedRows);
        if (isSelected) {
            newSelected.add(rowId);
        } else {
            newSelected.delete(rowId);
        }
        setSelectedRows(newSelected);
        onSelectionChange?.(Array.from(newSelected));
    }, [selectedRows, onSelectionChange]);

    // Apply optional client-side filtering and column filters
    const filteredData = useMemo(() => {
        let result = sortedData;

        if (filterMode === 'client') {
            result = filterRowsByColumn(result, columns, searchColumn, searchQuery);
        }

        // Skip client-side column filtering when server-side filters are enabled
        // In server mode, data is already filtered by the backend
        if (!serverSideFilters) {
            // Apply column filters (checkbox-based) only for client-side filtering
            const activeFilters = Object.entries(columnFilters).filter(([_, values]) => values.length > 0);
            if (activeFilters.length > 0) {
                result = result.filter((row: T) => {
                    return activeFilters.every(([columnKey, selectedValues]) => {
                        const column = columns.find(c => c.key === columnKey);
                        const cellValue = column?.accessor ? column.accessor(row) : row[columnKey];
                        const strValue = cellValue == null ? '' : String(cellValue);
                        return selectedValues.includes(strValue);
                    });
                });
            }
        }

        return result;
    }, [sortedData, columns, searchColumn, searchQuery, filterMode, columnFilters, serverSideFilters]);

    // Handle select all
    const handleSelectAll = useCallback((isSelected: boolean) => {
        if (isSelected) {
            const allIds = new Set(filteredData.map((item: T) => String(item[rowKey])));
            setSelectedRows(allIds);
            onSelectionChange?.(Array.from(allIds));
        } else {
            setSelectedRows(new Set());
            onSelectionChange?.([]);
        }
    }, [filteredData, rowKey, onSelectionChange]);

    // Column resize handlers
    const handleResizeStart = useCallback((e: React.MouseEvent, columnKey: string) => {
        e.preventDefault();
        e.stopPropagation();

        const headerElement = e.currentTarget.parentElement as HTMLElement;
        const rect = headerElement.getBoundingClientRect();

        resizeRef.current = {
            isResizing: true,
            startX: e.clientX,
            startWidth: columnWidths[columnKey] || rect.width,
            columnKey
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (!resizeRef.current.isResizing) return;

            const diff = e.clientX - resizeRef.current.startX;
            const newWidth = Math.max(60, resizeRef.current.startWidth + diff); // Min width 60px

            // Use the hook's setter - it handles persistence automatically
            setColumnWidth(resizeRef.current.columnKey, newWidth);
        };

        const handleMouseUp = () => {
            resizeRef.current.isResizing = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [columnWidths, setColumnWidth]);

    // Column drag and drop handlers
    const handleDragStart = useCallback((e: React.DragEvent, columnKey: string) => {
        // Don't allow dragging the actions column
        const column = columns.find(c => c.key === columnKey);
        if (column?.draggable === false) {
            e.preventDefault();
            return;
        }

        setDraggedColumn(columnKey);
        e.dataTransfer.effectAllowed = 'move';
    }, [columns]);

    const handleDragOver = useCallback((e: React.DragEvent, columnKey: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverColumn(columnKey);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, targetColumnKey: string) => {
        e.preventDefault();

        if (!draggedColumn || draggedColumn === targetColumnKey) {
            setDraggedColumn(null);
            setDragOverColumn(null);
            return;
        }

        const targetColumn = columns.find(c => c.key === targetColumnKey);
        if (targetColumn?.draggable === false) {
            setDraggedColumn(null);
            setDragOverColumn(null);
            return;
        }

        const newOrder = [...columnOrder];
        const draggedIndex = newOrder.indexOf(draggedColumn);
        const targetIndex = newOrder.indexOf(targetColumnKey);

        // Remove dragged column and insert at target position
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn);

        // Use the hook's setter - it handles persistence automatically
        setColumnOrder(newOrder);

        setDraggedColumn(null);
        setDragOverColumn(null);
    }, [draggedColumn, columnOrder, columns, setColumnOrder]);

    // Infinite scroll detection with throttling
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        let lastScrollTime = 0;
        const throttleMs = 100;

        const handleScroll = () => {
            const now = Date.now();

            // Throttle scroll events
            if (now - lastScrollTime < throttleMs) {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    handleScrollInternal();
                }, throttleMs);
                return;
            }

            lastScrollTime = now;
            handleScrollInternal();
        };

        const handleScrollInternal = () => {
            if (loading || !hasMore || !onLoadMore) return;

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            if (scrollTop + windowHeight >= documentHeight - 200) {
                onLoadMore();
            }
        };

        window.addEventListener('scroll', handleScroll, {passive: true});
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutId);
        };
    }, [loading, hasMore, onLoadMore]);

    const isAllSelected = filteredData.length > 0 && selectedRows.size === filteredData.length;
    const isIndeterminate = selectedRows.size > 0 && selectedRows.size < filteredData.length;

    return (
        <div
            className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Header with actions and search */}
            {showHeader && (
                <div
                    className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                        {/* Left side - Create button and bulk actions */}
                        <div className="flex items-center gap-2 flex-wrap">
                            {actions.create && onCreate && (
                                <Button
                                    onClick={onCreate}
                                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-200 px-4 py-2 font-medium text-sm rounded-md"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M12 4v16m8-8H4"/>
                                    </svg>
                                    <span className="hidden sm:inline">Create New</span>
                                    <span className="sm:hidden">Create</span>
                                </Button>
                            )}
                            {actions.bulkDelete && selectedRows.size > 0 && onBulkDelete && (
                                <Button
                                    onClick={() => onBulkDelete(Array.from(selectedRows))}
                                    className="!bg-red-500 hover:!bg-red-600 text-white shadow-md hover:shadow-lg transition-all duration-200 px-3 py-2 font-medium text-sm rounded-md"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                    </svg>
                                    <span className="hidden sm:inline">Delete Selected ({selectedRows.size})</span>
                                    <span className="sm:hidden">Delete ({selectedRows.size})</span>
                                </Button>
                            )}
                            {actions.bulkArchive && selectedRows.size > 0 && onBulkArchive && (
                                <Button
                                    onClick={() => onBulkArchive(Array.from(selectedRows))}
                                    className="!bg-amber-500 hover:!bg-amber-600 text-white shadow-md hover:shadow-lg transition-all duration-200 px-3 py-2 font-medium text-sm rounded-md"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                                    </svg>
                                    <span className="hidden sm:inline">Archive Selected ({selectedRows.size})</span>
                                    <span className="sm:hidden">Archive ({selectedRows.size})</span>
                                </Button>
                            )}
                        </div>

                        {/* Right side - Search and Record Count */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">

                            {/* Clear Filters Button - only show when filters are active */}
                            {Object.values(columnFilters).some(values => values && values.length > 0) && (
                                <button
                                    onClick={() => {
                                        clearAllFilters();
                                        if (serverSideFilters && onFiltersChange) {
                                            onFiltersChange({});
                                        }
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                    Clear Filters
                                </button>
                            )}

                            {/* Search with Column Selector */}
                            <div className="relative flex items-stretch flex-1 max-w-lg">
                                {/* Column selector dropdown */}
                                <div className="relative flex-shrink-0">
                                    <select
                                        value={searchColumn}
                                        onChange={(e) => {
                                            const newColumn = e.target.value;
                                            if (controlledSearchColumn !== undefined) {
                                                // Controlled mode - call onSearch with new column
                                                onSearch?.(searchQuery, newColumn);
                                            } else {
                                                // Uncontrolled mode - use internal handler
                                                internalSearch.setSearchColumn(newColumn);
                                            }
                                        }}
                                        className="h-full py-2 pl-3 pr-7 text-xs font-medium bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 border-2 border-r-0 border-gray-200 dark:border-gray-600 rounded-l-xl text-blue-700 dark:text-blue-300 cursor-pointer hover:from-blue-100 hover:to-blue-150 dark:hover:from-blue-800 dark:hover:to-blue-700 focus:outline-none focus:border-blue-500 focus:z-10 transition-all duration-200 appearance-none"
                                        title="Select search column"
                                    >
                                        {columns
                                            .filter(col => col.searchable !== false)
                                            .map(col => (
                                                <option key={col.key} value={col.key}>
                                                    {col.label}
                                                </option>
                                            ))
                                        }
                                    </select>
                                    {/* Custom dropdown arrow */}
                                    <svg
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-blue-500 dark:text-blue-400 pointer-events-none"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                                              d="M19 9l-7 7-7-7"/>
                                    </svg>
                                </div>

                                {/* Search input */}
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        placeholder={searchPlaceholderValue}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full h-full py-2 pl-9 pr-4 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border-2 border-l-0 border-gray-200 dark:border-gray-600 rounded-r-xl placeholder-gray-400 dark:placeholder-gray-500 hover:border-gray-300 dark:hover:border-gray-500 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-100 dark:focus:ring-blue-900 transition-all duration-200"
                                    />
                                    <svg
                                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto bg-white dark:bg-gray-800">
                <table className="w-full table-auto min-w-full">
                    <thead
                        className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 sticky top-0 z-10 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                        {/* Select all checkbox */}
                        <th className={`${compact ? 'w-8 px-2 py-1.5' : 'w-10 px-3 py-3'} text-left border-r border-gray-200 dark:border-gray-600`}>
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    ref={(input) => {
                                        if (input) input.indeterminate = isIndeterminate;
                                    }}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded transition-colors duration-200`}
                                />
                                <span className="sr-only">Select all</span>
                            </label>
                        </th>

                        {/* Column headers */}
                        {getOrderedColumns().map((column, index) => (
                            <th
                                key={column.key}
                                style={getColumnStyle(column)}
                                className={`${compact ? 'px-2 py-1.5' : 'px-4 py-3'} text-left ${compact ? 'text-[10px]' : 'text-xs'} font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 relative border-r border-gray-200 dark:border-gray-600 last:border-r-0 ${
                                    draggedColumn === column.key ? 'opacity-50' : ''
                                } ${dragOverColumn === column.key ? 'bg-blue-50 dark:bg-blue-900' : ''}`}
                                draggable={column.draggable !== false}
                                onDragStart={(e) => handleDragStart(e, column.key)}
                                onDragOver={(e) => handleDragOver(e, column.key)}
                                onDrop={(e) => handleDrop(e, column.key)}
                                onDragLeave={() => setDragOverColumn(null)}
                                onClick={() => {
                                    handleColumnClick(column.key);
                                    if (column.sortable !== false) {
                                        handleSort(column.key);
                                    }
                                }}
                            >
                                <div className="flex items-center justify-between group">
                                    <span
                                        className="font-medium text-left truncate pr-2 select-none">{column.label}</span>
                                    <div className="flex items-center gap-1">
                                        {/* Filter icon */}
                                        {column.filterable && (
                                            <button
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (openFilterColumn === column.key) {
                                                        setOpenFilterColumn(null);
                                                        setFilterSearch('');
                                                        setFilterDropdownPosition(null);
                                                    } else {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        setFilterDropdownPosition({
                                                            top: rect.bottom + 4,
                                                            left: rect.left
                                                        });
                                                        setOpenFilterColumn(column.key);
                                                        setFilterSearch('');
                                                    }
                                                }}
                                                className={`p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ${
                                                    columnFilters[column.key]?.length ? 'text-blue-600' : 'text-gray-400'
                                                }`}
                                                title="Filter"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd"
                                                          d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                                                          clipRule="evenodd"/>
                                                </svg>
                                            </button>
                                        )}
                                        {column.sortable !== false && (
                                            <div
                                                className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                                                <svg
                                                    className={`w-3 h-3 ${
                                                        sortColumn === column.key && sortDirection === 'asc'
                                                            ? 'text-blue-600'
                                                            : 'text-gray-400'
                                                    }`}
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"/>
                                                </svg>
                                                <svg
                                                    className={`w-3 h-3 -mt-1 ${
                                                        sortColumn === column.key && sortDirection === 'desc'
                                                            ? 'text-blue-600'
                                                            : 'text-gray-400'
                                                    }`}
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Resize handle */}
                                {(column.resizable !== false) && (
                                    <div
                                        className="absolute top-0 right-0 w-1 h-full bg-transparent hover:bg-blue-400 cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                        onMouseDown={(e) => handleResizeStart(e, column.key)}
                                    />
                                )}
                            </th>
                        ))}
                    </tr>
                    </thead>

                    <tbody className="bg-white dark:bg-gray-800">
                    {filteredData.length === 0 ? (
                        <tr>
                            <td colSpan={getOrderedColumns().length + 1} className="px-6 py-12 text-center">
                                <div
                                    className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                                    <svg className="w-12 h-12 mb-4 text-gray-300 dark:text-gray-600" fill="none"
                                         stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
                                    </svg>
                                    <p className="text-lg font-medium mb-1">{emptyMessage}</p>
                                    <p className="text-sm text-gray-400 dark:text-gray-500">No data to display at the
                                        moment</p>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        filteredData.map((row: T, index: number) => {
                            const rowId = row[rowKey];
                            const isSelected = selectedRows.has(rowId);
                            const isActiveRow = selectedRowId === rowId;
                            const isEven = index % 2 === 0;
                            const allRowIds = filteredData.map((r: T) => r[rowKey]);

                            const customRowClassName = rowClassName ? rowClassName(row, index) : '';

                            return (
                                <tr
                                    key={rowId}
                                    ref={(el) => {
                                        if (el) {
                                            rowRefsMap.current.set(rowId, el);
                                        } else {
                                            rowRefsMap.current.delete(rowId);
                                        }
                                    }}
                                    onClick={() => onRowClick?.(row)}
                                    onDoubleClick={() => onRowDoubleClick?.(row)}
                                    onContextMenu={(e) => {
                                        if (contextMenuActions && contextMenuActions.length > 0) {
                                            e.preventDefault();
                                            setContextMenu({x: e.clientX, y: e.clientY, item: row});
                                        }
                                    }}
                                    onMouseEnter={() => handleRowMouseEnter(rowId, index, allRowIds)}
                                    onMouseMove={() => handleRowMouseMove(rowId, index, allRowIds)}
                                    className={`
                      transition-colors duration-150 border-b border-gray-100 dark:border-gray-700 last:border-b-0 cursor-pointer select-none
                      ${customRowClassName}
                      ${isActiveRow
                                        ? 'bg-blue-100 dark:bg-blue-800 ring-2 ring-blue-500 dark:ring-blue-400 hover:bg-blue-200 dark:hover:bg-blue-700'
                                        : isSelected
                                            ? 'bg-blue-50 dark:bg-blue-900 hover:bg-blue-100 dark:hover:bg-blue-800'
                                            : isEven
                                                ? 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                : 'bg-gray-25 dark:bg-gray-900 hover:bg-gray-75 dark:hover:bg-gray-700'
                                    }
                    `}
                                >
                                    {/* Select checkbox */}
                                    <td className={`${compact ? 'w-8 px-2 py-1.5' : 'w-10 px-3 py-3'} border-r border-gray-200 dark:border-gray-600`}>
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={(e) => {
                                                    // Handle normal clicks (not drags)
                                                    if (!isDraggingRef.current && !hasMovedRef.current) {
                                                        handleRowSelect(rowId, e.target.checked);
                                                    }
                                                }}
                                                onMouseDown={(e) => handleCheckboxMouseDown(e, rowId, index, allRowIds)}
                                                className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded transition-colors duration-200`}
                                            />
                                            <span className="sr-only">Select row</span>
                                        </label>
                                    </td>

                                    {/* Data columns */}
                                    {getOrderedColumns().map((column) => (
                                        <td
                                            key={column.key}
                                            style={getColumnStyle(column)}
                                            className={`${compact ? 'px-2 py-1.5' : 'px-4 py-3'} ${compact ? 'text-xs' : 'text-sm'} text-gray-900 dark:text-gray-100 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-700 border-r border-gray-200 dark:border-gray-600 last:border-r-0`}
                                        >
                                            <div className={`font-normal ${
                                                column.key === 'description' || column.key === 'address' ? 'break-words leading-relaxed max-h-12 overflow-hidden' : 'overflow-hidden text-ellipsis whitespace-nowrap'
                                            }`}>
                                                {(() => {
                                                    const cellValue = column.accessor ? column.accessor(row) : (row as any)[column.key];
                                                    // Handle undefined/null values
                                                    if (cellValue == null || cellValue === undefined) {
                                                        return (
                                                            <span>-</span>
                                                        );
                                                    }
                                                    return column.render ? column.render(cellValue, row) : (
                                                        <span title={String(cellValue)}>
                                {String(cellValue)}
                              </span>
                                                    );
                                                })()}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            );
                        })
                    )}
                    </tbody>
                </table>
            </div>

            {/* Loading indicator */}
            {loading && (
                <div
                    className="flex justify-center items-center py-6 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-3">
                        <div
                            className="animate-spin rounded-full h-6 w-6 border-2 border-blue-200 border-t-blue-600"></div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Loading data...</span>
                    </div>
                </div>
            )}

            {/* Load more indicator */}
            {hasMore && !loading && (
                <div
                    className="flex justify-center items-center py-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
                        </svg>
                        <span className="font-medium">Scroll down to load more records</span>
                    </div>
                </div>
            )}

            {/* Context menu portal */}
            {contextMenu && contextMenuActions && contextMenuActions.length > 0 && createPortal(
                <div
                    ref={contextMenuRef}
                    className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg py-1 min-w-[140px]"
                    style={{top: contextMenu.y, left: contextMenu.x}}
                >
                    {contextMenuActions
                        .filter(action => !action.hidden?.(contextMenu.item))
                        .map((action, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    action.onClick(contextMenu.item);
                                    setContextMenu(null);
                                }}
                                disabled={action.disabled?.(contextMenu.item)}
                                className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                    action.disabled?.(contextMenu.item) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                } ${action.className || 'text-gray-700 dark:text-gray-300'}`}
                            >
                                {action.icon}
                                {action.label}
                            </button>
                        ))}
                </div>,
                document.body
            )}

            {/* Filter dropdown portal */}
            {openFilterColumn && filterDropdownPosition && (() => {
                const column = columns.find(c => c.key === openFilterColumn);
                if (!column) return null;

                // For server-side filters, use serverFilterOptions; otherwise extract from data
                let uniqueValues: [string, number][];
                if (serverSideFilters && getFilterOptions) {
                    // Use server-fetched options
                    uniqueValues = serverFilterOptions.map(opt => [opt.value, opt.count] as [string, number]);
                } else {
                    // Get unique values and their counts from client data
                    const valueCounts = new Map<string, number>();
                    sortedData.forEach((row: T) => {
                        const cellValue = column.accessor ? column.accessor(row) : row[column.key];
                        const strValue = cellValue == null ? '' : String(cellValue);
                        valueCounts.set(strValue, (valueCounts.get(strValue) || 0) + 1);
                    });
                    uniqueValues = Array.from(valueCounts.entries())
                        .sort((a, b) => a[0].localeCompare(b[0]));
                }

                // Filter by search
                const filteredValues = filterSearch
                    ? uniqueValues.filter(([val]) => val.toLowerCase().includes(filterSearch.toLowerCase()))
                    : uniqueValues;

                const selectedValues = columnFilters[column.key] || [];

                return createPortal(
                    <div
                        ref={filterDropdownRef}
                        className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg min-w-[280px]"
                        style={{top: filterDropdownPosition.top, left: filterDropdownPosition.left}}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {serverSideFilters ? 'Filter' : 'Local Filter'} For '{column.label}'
                            </div>
                        </div>

                        {/* Search input */}
                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                            <div className="relative">
                                <svg
                                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                </svg>
                                <input
                                    type="text"
                                    value={filterSearch}
                                    onChange={(e) => setFilterSearch(e.target.value)}
                                    placeholder=""
                                    className="w-full pl-9 pr-3 py-1.5 text-sm border border-blue-400 dark:border-blue-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Loading state for server-side filters */}
                        {loadingFilterOptions && (
                            <div className="flex items-center justify-center py-4">
                                <div
                                    className="animate-spin rounded-full h-5 w-5 border-2 border-blue-200 border-t-blue-600"></div>
                                <span className="ml-2 text-sm text-gray-500">Loading options...</span>
                            </div>
                        )}

                        {/* Header row */}
                        {!loadingFilterOptions && (
                            <div
                                className="flex items-center px-3 py-1.5 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
                                <input
                                    type="checkbox"
                                    checked={selectedValues.length === uniqueValues.length && uniqueValues.length > 0}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setColumnFilter(column.key, uniqueValues.map(([v]) => v));
                                        } else {
                                            setColumnFilter(column.key, []);
                                        }
                                    }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded mr-3"
                                />
                                <span
                                    className="flex-1 text-xs font-medium text-gray-600 dark:text-gray-400">Value</span>
                                <span
                                    className="text-xs font-medium text-gray-600 dark:text-gray-400 w-12 text-right">Count</span>
                            </div>
                        )}

                        {/* Values list */}
                        {!loadingFilterOptions && (
                            <div className="max-h-64 overflow-y-auto">
                                {filteredValues.map(([value, count]) => (
                                    <label
                                        key={value || '__empty__'}
                                        className="flex items-center px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedValues.includes(value)}
                                            onChange={(e) => {
                                                const currentFilters = columnFilters[column.key] || [];
                                                if (e.target.checked) {
                                                    setColumnFilter(column.key, [...currentFilters, value]);
                                                } else {
                                                    setColumnFilter(column.key, currentFilters.filter(v => v !== value));
                                                }
                                            }}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded mr-3"
                                        />
                                        <span
                                            className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">{value || '(empty)'}</span>
                                        <span
                                            className="text-sm text-gray-500 dark:text-gray-400 w-12 text-right">{count}</span>
                                    </label>
                                ))}
                            </div>
                        )}

                        {/* Footer */}
                        <div
                            className="px-3 py-2 border-t border-gray-200 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400">
                            {'Select a checkbox to filter rows'}
                        </div>
                    </div>,
                    document.body
                );
            })()}
        </div>
    );
};
